/* global trapFocus, removeTrapFocus */

/**
 * <coupons-modal>
 * A coupons list dialog (centered modal on desktop, bottom sheet on mobile).
 * Opened via the document-level `theme.events.couponsOpen` event fired by the
 * `.js-open-coupons` trigger. Applies / removes discount codes through
 * /cart/update.js and triggers a full cart re-render.
 *
 * Coupons list interaction model (v1.5):
 *   <coupons-list>  ── coupon:action ──>  this modal
 *   <coupons-list>  <── coupon:applied ──  this modal
 *   The modal never reaches into <coupons-list>'s internal DOM.
 */
if (!customElements.get('coupons-modal')) {
  class CouponsModal extends HTMLElement {
    appliedCode = ''
    shouldUpdateCart = false

    constructor() {
      super();

      this.context = this.dataset.context || 'drawer';
      this.panel = this.querySelector('.coupons-modal__panel');
      this.backdrop = this.querySelector('.coupons-modal__backdrop');
      this.errorEl = this.querySelector('.js-coupons-error');

      this.openHandler = (e) => {
        if (e.detail?.context && e.detail.context !== this.context) return;
        this.open(e.detail?.opener || null);
      };
      this.keyupHandler = (e) => e.key === 'Escape' && this.close();
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback () {
      document.addEventListener(theme.events.couponsOpen, this.openHandler);
      this.addEventListener('click', this.handleClick);

      // <coupons-list> inside us listens for `coupon:action`; we publish
      // `coupon:applied` back to it. Both directions stay within this element
      // thanks to the scoped `this` listener below.
      this.couponsList = this.querySelector('coupons-list');
      if (this.couponsList) {
        this.couponsList.addEventListener(theme.events.couponAction, this.onCouponAction);
      }

      this.manualForm = this.querySelector('[data-coupons-manual]');
      if (this.manualForm) {
        this.input = this.manualForm.querySelector('.js-coupon-input');
        this.manualSubmitBtn = this.manualForm.querySelector('.js-apply-manual-coupon');

        this.appliedCode = (this.input.value || '').trim();
        this.updateManualButtonState();

        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.manualSubmitBtn.click();
          }
        });
        this.input.addEventListener('input', () => this.updateManualButtonState());
      }

      // De-dup: keep at most one coupons-modal per context in the document.
      // (When a section re-render replaces the original parent, a previous
      // instance may still be teleported onto <body>.)
      Array.from(document.querySelectorAll('coupons-modal'))
        .filter((el) => el !== this && el.dataset.context === this.context && document.body.contains(el))
        .forEach((el) => el.remove());

      // Teleport bookkeeping — close() restores us here.
      this.originalParent = this.parentNode;
      this.originalNextSibling = this.nextSibling;
    }

    disconnectedCallback () {
      document.removeEventListener(theme.events.couponsOpen, this.openHandler);
      this.couponsList?.removeEventListener(theme.events.couponAction, this.onCouponAction);
      // this.unlockScroll();
    }

    onCouponAction = (e) => {
      const { code, action, button } = e.detail;
      this.applyDiscount(action === 'remove' ? '' : code, button);
    }

    open (opener) {
      this.opener = opener;

      // Teleport to <body> so `position: fixed` panel + backdrop use the
      // viewport (not the <cart-drawer> transform) as containing block.
      if (this.parentNode !== document.body) {
        this.originalParent = this.parentNode;
        this.originalNextSibling = this.nextSibling;
        document.body.appendChild(this);
      }

      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.offsetHeight; // eslint-disable-line no-unused-expressions
      this.classList.add('is-open');

      // this.lockScroll();
      trapFocus(this, this.querySelector('.coupons-modal__close'));

      document.addEventListener('keyup', this.keyupHandler);
    }

    close () {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      // this.unlockScroll();
      removeTrapFocus(this.opener);
      document.removeEventListener('keyup', this.keyupHandler);
      this.clearError();
      if (this.shouldUpdateCart) this.refreshCart();

      const onEnd = (e) => {
        if (e.target !== this.panel) return;
        this.hidden = true;
        this.restoreToOrigin();
        this.panel.removeEventListener('transitionend', onEnd);
      };
      this.panel.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!this.classList.contains('is-open')) {
          this.hidden = true;
          this.restoreToOrigin();
        }
      }, 400);
    }

    restoreToOrigin () {
      if (this.parentNode === document.body && this.originalParent && document.body.contains(this.originalParent)) {
        this.originalParent.insertBefore(this, this.originalNextSibling);
      }
    }

    handleClick (evt) {
      if (evt.target.closest('.js-close-coupons')) {
        this.close();
        return;
      }

      // Manual entry: Apply / Cancel on the input. Rerouted through the same
      // applyDiscount() as a card click — uniform code path.
      const manualBtn = evt.target.closest('.js-apply-manual-coupon');
      if (manualBtn) {
        const code = ((this.input?.value) || '').trim();
        if (!code) { this.clearError(); return; }
        const payload = manualBtn.dataset.mode === 'cancel' ? '' : code;
        this.applyDiscount(payload, manualBtn);
      }
    }

    async applyDiscount (code, button) {
      this.clearError();
      // The list already added is-loading + disabled on the card button when it
      // dispatched `coupon:action`. We MUST fire `coupon:resolved` from the
      // finally block so the list restores the button no matter what — success,
      // business-error (invalid code), or network-error.
      try {
        const response = await fetch(`${theme.routes.cartUpdate}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code })
        });
        const cart = await response.json();
        this.shouldUpdateCart = true;

        if (!code) {           // explicit Remove
          this.appliedCode = '';
          this.updateManualButtonState();
          this.publishApplied('');
          this.close();
          return;
        }

        // Did Shopify actually take the code? If not, surface "invalid".
        const codeDown = code.toLowerCase();
        const applied = (cart.cart_level_discount_applications || []).some(
          (d) => (d.discount_application.title || '').toLowerCase() === codeDown
        );
        const applicable = (cart.discount_codes || []).some(
          (d) => d.applicable && (d.code || '').toLowerCase() === codeDown
        );

        if (!applied && !applicable) {
          this.showError(theme.strings.coupons ? theme.strings.coupons.invalid : '');
          this.appliedCode = '';
          this.updateManualButtonState();
          this.publishApplied('');
          return;
        }

        this.appliedCode = code;
        this.updateManualButtonState();
        this.publishApplied(code);
        this.close();
      } catch (err) {
        this.showError(err.message || (theme.strings.coupons && theme.strings.coupons.error) || '');
        this.appliedCode = '';
        this.updateManualButtonState();
        this.publishApplied('');
      } finally {
        this.publishResolved(button);
      }
    }

    /** Push the current applied code down to <coupons-list> via the event bus. */
    publishApplied (code) {
      this.couponsList?.dispatchEvent(new CustomEvent(theme.events.couponApplied, {
        bubbles: false,
        detail: { code: code || '' }
      }));
    }

    /** Tell the list we're done with `button` (success or error) so it can restore state. */
    publishResolved (button) {
      this.couponsList?.dispatchEvent(new CustomEvent(theme.events.couponResolved, {
        bubbles: false,
        detail: { button }
      }));
    }

    refreshCart () {
      if (this.context === 'drawer') {
        document.dispatchEvent(new CustomEvent(theme.events.cartDrawerRefresh, { bubbles: true }));
      } else {
        const cartItems = document.getElementById('cart-items') || document.querySelector('cart-items');
        if (cartItems && typeof cartItems.refresh === 'function') {
          cartItems.refresh();
        } else {
          window.location.reload();
        }
      }
    }

    updateManualButtonState () {
      if (!this.manualSubmitBtn || !this.input) return;
      const inputValue = (this.input.value || '').trim().toLowerCase();
      const appliedValue = (this.appliedCode || '').trim().toLowerCase();
      const shouldCancel = Boolean(appliedValue) && inputValue === appliedValue;

      this.manualSubmitBtn.dataset.mode = shouldCancel ? 'cancel' : 'apply';
      this.manualSubmitBtn.textContent = shouldCancel
        ? (this.getCouponString('cancel') || 'Cancel')
        : (this.getCouponString('apply') || 'Apply');
    }

    getCouponString (key) {
      return (theme.strings?.coupons?.[key]) || '';
    }

    showError (message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || (theme.strings?.coupons?.error) || '';
      this.errorEl.hidden = false;
    }

    clearError () {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    lockScroll () {
      this.scrollY = window.scrollY;
      document.body.classList.add('fixed');
      document.body.style.top = `-${this.scrollY}px`;
      document.documentElement.style.height = '100vh';
    }

    unlockScroll () {
      if (!document.body.classList.contains('fixed')) return;
      document.documentElement.style.height = '';
      document.body.classList.remove('fixed');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY || 0);
    }
  }

  customElements.define('coupons-modal', CouponsModal);
}

/**
 * Delegated trigger handler. The trigger button lives inside the cart summary
 * container which is re-rendered on cart updates, so we delegate from document
 * rather than binding per-instance.
 */
if (!window.__couponsTriggerBound) {
  window.__couponsTriggerBound = true;
  document.addEventListener('click', (evt) => {
    const trigger = evt.target.closest('.js-open-coupons');
    if (!trigger) return;
    evt.preventDefault();
    evt.stopPropagation();

    const wrapper = trigger.closest('.coupons');
    const context = wrapper ? wrapper.dataset.context : 'drawer';
    document.dispatchEvent(new CustomEvent(theme.events.couponsOpen, {
      detail: { opener: trigger, context }
    }));
  });
}
